/**
 * Automotivebypass fitment core
 *
 * One parser / one matcher for:
 * - home vehicle selector
 * - collection vehicle finder
 * - PDP vehicle finder
 * - /pages/vehicle-results
 *
 * Canonical product value (custom.fitment_csv):
 *   Ford|F-150|2021-2023|3.5L V6|Confirmed application
 *
 * Legacy values are still accepted:
 * - JSON array from custom.fitment
 * - JSON string stored in custom.fitment_csv
 * - pipe rows without a note
 */
(() => {
  if (window.ABFitment) return;

  const clean = (value) => String(value ?? '').trim();

  const canonMake = (value) => {
    const raw = clean(value).toUpperCase().replace(/\s+/g, ' ');
    if (raw === 'CHEVROLET' || raw === 'CHEVY') return 'CHEVROLET';
    return raw;
  };

  const canonModel = (value) => clean(value).toUpperCase().replace(/\s+/g, ' ');

  const MAKE_DISPLAY = {
    FORD: 'Ford',
    CHEVROLET: 'Chevrolet',
    GMC: 'GMC',
    RAM: 'Ram',
    BMW: 'BMW',
    TOYOTA: 'Toyota',
    HONDA: 'Honda',
    NISSAN: 'Nissan',
    VOLKSWAGEN: 'Volkswagen',
    SUBARU: 'Subaru',
    HYUNDAI: 'Hyundai',
    KIA: 'Kia',
    JEEP: 'Jeep',
    DODGE: 'Dodge',
    CHRYSLER: 'Chrysler',
    BUICK: 'Buick',
    CADILLAC: 'Cadillac',
    ACURA: 'Acura',
    AUDI: 'Audi',
    LEXUS: 'Lexus',
    LINCOLN: 'Lincoln',
    VOLVO: 'Volvo'
  };

  const displayMake = (value) => {
    const key = canonMake(value);
    return MAKE_DISPLAY[key] || key.toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const expandYears = (value) => {
    const raw = clean(value).replace(/[–—]/g, '-');
    const range = raw.match(/^(\d{4})\s*-\s*(\d{4})$/);
    if (range) {
      const start = Number(range[1]);
      const end = Number(range[2]);
      const out = [];
      for (let year = Math.min(start, end); year <= Math.max(start, end); year += 1) {
        out.push(String(year));
      }
      return out;
    }
    if (/^\d{4}$/.test(raw)) return [raw];
    return raw.split(/[,|]/).map(clean).filter(Boolean);
  };

  const normalizeRow = (row) => {
    if (!row || typeof row !== 'object') return null;
    const make = clean(row.make ?? row.Make);
    const model = clean(row.model ?? row.Model);
    const year = clean(row.year ?? row.Year);
    if (!make || !model || !year) return null;
    return {
      make,
      model,
      year,
      trim_engine: clean(row.trim_engine ?? row.trim ?? row.engine ?? row.Trim),
      note: clean(row.note ?? row.Note)
    };
  };

  const parse = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.flatMap((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const row = normalizeRow(item);
          return row ? [row] : [];
        }
        return parse(item);
      });
    }

    let raw = String(value).trim();
    if (!raw) return [];

    // Accept JSON arrays, JSON strings-of-arrays, and quoted pipe text.
    if (raw.startsWith('[') || raw.startsWith('"')) {
      try {
        let parsed = JSON.parse(raw);
        if (typeof parsed === 'string') {
          raw = parsed;
          try {
            const nested = JSON.parse(raw);
            if (Array.isArray(nested)) return nested.map(normalizeRow).filter(Boolean);
          } catch (error) {
            // decoded string is pipe text; continue below
          }
        } else if (Array.isArray(parsed)) {
          return parsed.map(normalizeRow).filter(Boolean);
        }
      } catch (error) {
        // fall through to pipe parsing
      }
    }

    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#'))
      .map((line) => {
        const parts = line.split('|').map(clean);
        if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return null;
        return {
          make: parts[0],
          model: parts[1],
          year: parts[2],
          trim_engine: parts[3] || '',
          note: parts.slice(4).join(' | ')
        };
      })
      .filter(Boolean);
  };

  const build = (value) => {
    const hierarchy = {};

    parse(value).forEach((row) => {
      const make = displayMake(row.make);
      const model = canonModel(row.model);
      if (!make || !model) return;

      if (!hierarchy[make]) hierarchy[make] = {};
      if (!hierarchy[make][model]) hierarchy[make][model] = [];

      expandYears(row.year).forEach((year) => {
        if (!hierarchy[make][model].includes(year)) hierarchy[make][model].push(year);
      });
    });

    Object.keys(hierarchy).forEach((make) => {
      Object.keys(hierarchy[make]).forEach((model) => {
        hierarchy[make][model].sort((a, b) => Number(a) - Number(b));
      });
    });

    // Trim / Engine is product-specific and must not be aggregated globally.
    return { hierarchy, trims: {} };
  };

  const match = (value, selection) => {
    const wantedMake = canonMake(selection?.make);
    const wantedModel = canonModel(selection?.model);
    const wantedYear = clean(selection?.year);
    const wantedTrim = clean(selection?.trim ?? selection?.engine);

    if (!wantedMake || !wantedModel || !wantedYear) return false;

    return parse(value).some((row) => {
      if (canonMake(row.make) !== wantedMake) return false;
      if (canonModel(row.model) !== wantedModel) return false;
      if (!expandYears(row.year).includes(wantedYear)) return false;

      // Empty trim is a wildcard. This fixes the old bug where a product
      // with trim_engine disappeared when the URL had no trim parameter.
      if (!wantedTrim) return true;
      if (!row.trim_engine) return true;
      return row.trim_engine.toUpperCase() === wantedTrim.toUpperCase();
    });
  };

  window.ABFitment = {
    clean,
    canonMake,
    canonModel,
    displayMake,
    expandYears,
    parse,
    build,
    match
  };
})();


/* AB PDP fitment table display formatting */
(() => {
  const formatFitmentTable = () => {
    const root = document.querySelector('[id^="AutomotivebypassPdp-"]');
    if (!root) return;

    root.querySelectorAll('.abp__fitment-row').forEach((row) => {
      if (row.dataset.abTrimRowsFormatted === 'true') return;
      const cells = row.querySelectorAll('[role="cell"]');
      if (cells.length < 5) return;

      const trimCell = cells[3];
      const engines = trimCell.textContent.split('/').map((value) => value.trim()).filter(Boolean);
      if (engines.length > 1) {
        trimCell.textContent = engines[0];
        let previous = row;
        engines.slice(1).forEach((engine) => {
          const nextRow = row.cloneNode(true);
          nextRow.querySelectorAll('[role="cell"]')[3].textContent = engine;
          previous.after(nextRow);
          previous = nextRow;
          nextRow.dataset.abTrimRowsFormatted = 'true';
        });
      }
      row.dataset.abTrimRowsFormatted = 'true';
    });

    if (!root.querySelector('[data-ab-fitment-single-line-style]')) {
      const style = document.createElement('style');
      style.setAttribute('data-ab-fitment-single-line-style', '');
      style.textContent = [
        '#'+root.id+' .abp__fitment-head,',
        '#'+root.id+' .abp__fitment-row{grid-template-columns:1.05fr 1.25fr .65fr 1.2fr 3.2fr;min-width:1300px;}',
        '#'+root.id+' .abp__fitment-note,',
        '#'+root.id+' .abp__fitment-note-text{white-space:nowrap!important;overflow-wrap:normal!important;word-break:normal!important;}'
      ].join('');
      root.appendChild(style);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', formatFitmentTable, { once: true });
  } else {
    formatFitmentTable();
  }
})();
